import { Link } from "@tanstack/react-router";
import {
  ArrowUpRight,
  BookOpen,
  Briefcase,
  HelpCircle,
  Mail,
  Mic,
  Headphones,
  Sparkles,
  User,
  type LucideIcon,
} from "lucide-react";

type PageKey =
  | "about"
  | "podcast"
  | "book"
  | "ventures"
  | "faq"
  | "contact"
  | "guest"
  | "aion";

const PAGES: Record<
  PageKey,
  { to: string; icon: LucideIcon; title: string; desc: string }
> = {
  about: {
    to: "/about",
    icon: User,
    title: "About Raj Shamani",
    desc: "Full biography — age, education, family, net worth and career timeline.",
  },
  podcast: {
    to: "/podcast",
    icon: Headphones,
    title: "All Podcast Episodes",
    desc: "Every Figuring Out episode with summaries, transcripts and video.",
  },
  book: {
    to: "/build-dont-talk",
    icon: BookOpen,
    title: "Build, Don't Talk",
    desc: "Chapter-by-chapter summary, key ideas and where to buy the book.",
  },
  ventures: {
    to: "/ventures",
    icon: Briefcase,
    title: "Ventures & Investments",
    desc: "House of X, Shamani Industries and the angel investment portfolio.",
  },
  faq: {
    to: "/faq",
    icon: HelpCircle,
    title: "Raj Shamani FAQ",
    desc: "Quick answers to the most-asked questions about Raj Shamani.",
  },
  contact: {
    to: "/contact",
    icon: Mail,
    title: "Contact Raj Shamani",
    desc: "Brand partnerships, speaking enquiries and official email.",
  },
  aion: {
    to: "/aion",
    icon: Sparkles,
    title: "Aion — AI Course",
    desc: "The cinematic AI course Raj backs: prompt engineering, agents and leverage. Entry ₹11.",
  },
  guest: {
    to: "/guest",
    icon: Mic,
    title: "Be a Guest",
    desc: "Pitch yourself for a Figuring Out episode — how selection works.",
  },
};

export function RelatedPages({
  pages,
  heading = "Related pages",
  className = "",
}: {
  pages: PageKey[];
  heading?: string;
  className?: string;
}) {
  return (
    <section className={`mx-auto max-w-5xl px-6 pb-20 ${className}`}>
      <h2 className="text-2xl md:text-3xl font-bold mb-3">{heading}</h2>
      <p className="text-muted-foreground mb-8 max-w-2xl">
        Keep reading on the pages most closely connected to this topic.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {pages.map((key) => {
          const p = PAGES[key];
          return (
            <Link
              key={key}
              to={p.to}
              className="group block p-5 rounded-xl bg-card border border-border hover:border-gold/50 transition"
            >
              <p.icon size={18} className="text-gold mb-3" />
              <div className="font-bold mb-2 inline-flex items-center gap-2">
                {p.title}
                <ArrowUpRight
                  size={14}
                  className="text-gold group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition"
                />
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {p.desc}
              </p>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
